import {createContext, useContext, useState, useCallback, useMemo, useEffect, useRef} from 'react';

import {MessagingInboxProvider, useMessagingInbox} from "@edu-si/messaging";

import twilioService, {LEGACY_TWILIO_ROUTE} from "./twilio-service"
import featureFlagService from "../../generic/hooks/use-feature-flag/feature-flag-service";
import useFeatureFlag from "../../generic/hooks/use-feature-flag/use-feature-flag";
import useStatus from "../../generic/hooks/use-status/use-status";
import defaultMessagingService from "../../messaging/twilio/messaging-service-inbox/messaging-inbox-service";

const MESSAGING_SERVICE_FEATURE_FLAG = "MessagingService";
const defaultLegacyService = twilioService(LEGACY_TWILIO_ROUTE);
const defaultFeatureFlagService = featureFlagService();
const injectedServiceFlagService = {
  loadFlag: async () => false,
  reset: () => {}
};



const useTwilio = (userIdentifier, service) => {
  const [inbox, setInbox] = useState(null);
  const [hasTwilio,setHasTwilio] = useState(false);
  const [checkTimeStamp, setCheckTimeStamp] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const providerContext = useMemo(() => ({userIdentifier, service}), [userIdentifier, service]);
  const providerContextRef = useRef(providerContext);
  providerContextRef.current = providerContext;

  const {
    status,
    setSavingStatus:loadingInbox,
    setErrorStatus:inboxError,
    setResetStatus:resetInboxStatus,
  } = useStatus();

  useEffect(() => {
    let active = true;
    const fetchHasTwilio = async () => {
      if (!userIdentifier) {
        if (active) {
          setHasTwilio(false);
        }
        return;
      }

      try {
        const loadedHasTwilio = await service.loadHasTwilio();
        if (active) {
          setHasTwilio(loadedHasTwilio === true);
        }
      } catch (e) {
        if (active) {
          setHasTwilio(false);
        }
      }
    };

    fetchHasTwilio();
    return () => {
      active = false;
    };
  }, [userIdentifier, service]);

  useEffect(() => {
    let active = true;
    const fetchInbox = async () => {
      if (!userIdentifier) {
        if (active) {
          setInbox(null);
          setCheckTimeStamp(null);
          setSelectedConversationId(null);
          resetInboxStatus();
        }
        return;
      }

      try {
        loadingInbox();
        const twilioLoad = await service.loadInbox();
        if (active) {
          resetInboxStatus();
          setInbox(twilioLoad.inbox);
          setCheckTimeStamp(twilioLoad.timeStamp); // store the latest timestamp from the server (currently a Date)
          setSelectedConversationId(null);
        }
      } catch (e) {
        if (active) {
          inboxError(e);
          setInbox(null);
          setCheckTimeStamp(null);
          setSelectedConversationId(null);
        }
      }
    };
    fetchInbox();
    return () => {
      active = false;
    };
  }, [userIdentifier, service,
    loadingInbox, resetInboxStatus, inboxError]);

  const {
    status: checkStatus,
    setSavingStatus: checkingForNewMessages,
    setErrorStatus: checkError,
    setResetStatus: resetCheckStatus,
  } = useStatus();

  const checkForNewMessages = useCallback(async () => {
    if (!userIdentifier) { return; }
    const needsFullInbox = !checkTimeStamp || !Array.isArray(inbox);
    try {
      checkingForNewMessages();
      const newMessagesData = await service.loadInbox(needsFullInbox ? undefined : checkTimeStamp);
      if (providerContextRef.current !== providerContext) { return; }
      resetInboxStatus();
      setInbox((prevInbox) => {
        if (needsFullInbox) { return newMessagesData.inbox; }
        if (!Array.isArray(prevInbox)) { return prevInbox; }
        const updatedInbox = [...prevInbox];
        newMessagesData?.inbox?.forEach(newConversation => {
          const existingConversationIndex = updatedInbox
            .findIndex(existingConversation => existingConversation.conversationId === newConversation.conversationId);
          if (existingConversationIndex >= 0) {
            const existingConversation = {...updatedInbox[existingConversationIndex]};
            // Replace messages after the previous refresh timestamp with the server's latest results.
            const originalMessagesOnExisting = existingConversation.messages.filter(
              message => message.dateStamp <= checkTimeStamp);
            const newMessages = [...originalMessagesOnExisting, ...newConversation.messages];
            updatedInbox[existingConversationIndex] = {
              ...existingConversation,
              messages:newMessages
            };
          } else {
            updatedInbox.push(newConversation);
          }
        });
        return updatedInbox;
      });
      setCheckTimeStamp(newMessagesData.timeStamp);
      resetCheckStatus();
    } catch (e) {
      if (providerContextRef.current === providerContext) {
        checkError(e);
      }
    }
  }, [
    userIdentifier,
    checkTimeStamp,
    inbox,
    service,
    providerContext,
    checkingForNewMessages,
    resetCheckStatus,
    resetInboxStatus,
    checkError
  ]);

  const {
    status: replyStatus,
    setSavingStatus: sendingReply,
    setErrorStatus: replyError,
    setResetStatus: resetReplyStatus,
  } = useStatus();

  const replyToThread = useCallback(async (thread, content) => {
    if (!Array.isArray(inbox)) {return;}

    const {
      conversationId,
      number
    } = thread;

    try {
      sendingReply();
      const result = await service.sendReply(number, content);
      if (providerContextRef.current !== providerContext) { return true; }
      if (result.errorMessage) {
        //this one failed.
        replyError(result.errorMessage);
        return false;
      }
      //update the inbox data to show this message.
      setInbox((prevInbox) => {
        if (!Array.isArray(prevInbox)) { return prevInbox; }
        const updatedInbox = prevInbox.map(thread => {
          if (thread.conversationId === conversationId) {
            return {
              ...thread,
              messages: [...thread.messages, result]
            };
          }
          return thread;
        });
        return updatedInbox;
      });
      resetReplyStatus();
      return true;
    } catch (e) {
      if (providerContextRef.current === providerContext) {
        replyError(e);
      }
      return false;
    }
  }, [inbox,service,providerContext,//note: inbox dependency is just a paranoid safety check
    //making sure we never run this if we don't have an actual inbox.
    sendingReply, resetReplyStatus, replyError]);

  const {
    status: openStatus,
    setSavingStatus: openingThread,
    setErrorStatus: openError,
    setResetStatus: resetOpenStatus,
  } = useStatus();

  const openThread = useCallback(async (conversationId) => {
    if (!Array.isArray(inbox)) {return;}
    const thread = inbox.find(thread => thread.conversationId === conversationId);
    if (thread?.messages.every(message => message.read)) {
      setSelectedConversationId(conversationId);
      return;
    }
    try {
      const {
        number
      } = thread;
      openingThread();
      await service.setThreadRead(number);
      if (providerContextRef.current !== providerContext) { return; }
      setInbox((prevInbox) => {
        if (!Array.isArray(prevInbox)) { return prevInbox; }
        const updatedInbox = prevInbox.map(thread => {
          if (thread.conversationId === conversationId) {
            return {
              ...thread,
              messages: thread.messages.map(message => ({
                ...message,
                read: true
              }))
            };
          }
          return thread;
        });
        setSelectedConversationId(conversationId);
        return updatedInbox;
      });
      resetOpenStatus();
    } catch (e) {
      if (providerContextRef.current === providerContext) {
        setSelectedConversationId(conversationId);//not the end of the world
        //we can let them still act on the thread.
        openError(e);
      }
    }
    //note: dependency on inbox is only for a paranoid safety check, to make sure we never run this if we don't have an actual inbox.
  }, [inbox,service, providerContext, openingThread, resetOpenStatus, openError]);

  const {
    status: deleteStatus,
    setResetStatus: resetDeleteStatus,
  } = useStatus();

  const deleteThread = useCallback(() => {},[]);

  useEffect(() => {
    resetCheckStatus();
    resetReplyStatus();
    resetOpenStatus();
  }, [providerContext, resetCheckStatus, resetReplyStatus, resetOpenStatus]);

  const unreadCount = useMemo(() => {
    if (!Array.isArray(inbox)) {
      return 0;
    }
    return inbox.reduce((count, thread) => {
      const unreadMessages = thread.messages.reduce((msgCount, message) => {
        return (!message.read && !message.isFromWise) ? msgCount + 1 : msgCount;
      }, 0);
      return count + unreadMessages;
    }, 0);
  }, [inbox]);

  return {
    inboxStateKey: userIdentifier,
    selectedConversationId,
    status,
    inbox,
    replyToThread,
    openThread,
    openStatus,
    resetOpenStatus,
    deleteThread,
    deleteStatus,
    resetDeleteStatus,
    unreadCount,
    replyStatus,
    resetReplyStatus,
    hasTwilio,
    checkForNewMessages,
    checkStatus,
    resetCheckStatus,
  }
}

const TwilioContext = createContext();

const InboxContextBridge = ({children, legacy, identifier, messagingServiceEnabled}) => {
  const inbox = useMessagingInbox();
  return <TwilioContext.Provider value={{
    ...legacy,
    inboxStateKey: identifier,
    messagingServiceEnabled,
    ...(messagingServiceEnabled ? {
      unreadCount: inbox.unreadCountByChannel.SMS,
      checkForNewMessages: inbox.refresh
    } : {})
  }}>{children}</TwilioContext.Provider>;
};

const TwilioProvider = ({
  identifier,
  children,
  service,
  messagingService = defaultMessagingService,
  legacyService = defaultLegacyService,
  featureFlagService:providedFeatureFlagService = defaultFeatureFlagService
}) => {
  const useFlagService = service ? injectedServiceFlagService : providedFeatureFlagService;
  const {
    status: messagingServiceFlagStatus,
    loadedFlag
  } = useFeatureFlag(MESSAGING_SERVICE_FEATURE_FLAG, false, useFlagService);
  const messagingServiceEnabled = !service && !messagingServiceFlagStatus.error && loadedFlag === true;
  const flagResolved = Boolean(service) || loadedFlag !== undefined || messagingServiceFlagStatus.success ||
    Boolean(messagingServiceFlagStatus.error);
  const hook = useTwilio(flagResolved && !messagingServiceEnabled ? identifier : undefined, service ?? legacyService);

  return (
    <MessagingInboxProvider identityKey={identifier ?? ""}
      enabled={flagResolved && messagingServiceEnabled && Boolean(identifier)} service={messagingService}>
      <InboxContextBridge legacy={hook} identifier={identifier} messagingServiceEnabled={messagingServiceEnabled}>
        {children}
      </InboxContextBridge>
    </MessagingInboxProvider>
  )
}

const useTwilioProvider = () => {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    throw new Error('useTwilioProvider must be used within a TwilioProvider');
  }
  return context;
}


export {useTwilioProvider, TwilioProvider};
